import { useMemo, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Layout, Button, Select, Modal, Spin, message } from 'antd';
import { TagFilterPanel } from '../../../components/TagFilterPanel';
import { useTranslation } from '../../../i18n';
import { getSellerShops, createSellerAuthorizationUrl, unbindSellerShop } from '../../../store/request';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import type { SellerAuthShop } from '../../../types/seller';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationOutlined,
  MessageOutlined,
  PlusOutlined,
  SafetyOutlined,
} from '@ant-design/icons';

type ShopType = 'local' | 'cross';

type RegionTheme = {
  iconBg: string;
  topAccent: string;
  regionTag: string;
};

const regionThemes: Record<string, RegionTheme> = {
  vn: {
    iconBg: 'bg-[#596ae8]',
    topAccent: 'bg-[#596ae8]',
    regionTag: 'bg-[#596ae8]/10 text-[#596ae8]',
  },
  th: {
    iconBg: 'bg-[#8b5cf6]',
    topAccent: 'bg-[#8b5cf6]',
    regionTag: 'bg-[#8b5cf6]/10 text-[#8b5cf6]',
  },
  id: {
    iconBg: 'bg-[#0ea5e9]',
    topAccent: 'bg-[#0ea5e9]',
    regionTag: 'bg-[#0ea5e9]/10 text-[#0ea5e9]',
  },
  my: {
    iconBg: 'bg-[#14b8a6]',
    topAccent: 'bg-[#14b8a6]',
    regionTag: 'bg-[#14b8a6]/10 text-[#14b8a6]',
  },
  ph: {
    iconBg: 'bg-[#f59e0b]',
    topAccent: 'bg-[#f59e0b]',
    regionTag: 'bg-[#f59e0b]/10 text-[#f59e0b]',
  },
  sg: {
    iconBg: 'bg-[#64748b]',
    topAccent: 'bg-[#64748b]',
    regionTag: 'bg-[#64748b]/10 text-[#64748b]',
  },
};

const defaultRegionTheme: RegionTheme = {
  iconBg: 'bg-slate-500',
  topAccent: 'bg-slate-500',
  regionTag: 'bg-slate-100 text-slate-600',
};

type AuthorizedShop = {
  kind: 'shop';
  id: string;
  shopId: string;
  region: string;
  regionCode: string;
  regionLabel: string;
  name: string;
  shopType?: ShopType;
  storeCode: string;
  sellerEmail?: string;
  authorizedAt?: string;
};

type EmptySlot = {
  kind: 'empty';
  id: string;
};

type ShopSlot = AuthorizedShop | EmptySlot;

function marketLabel(region: string, labels: Record<string, string>) {
  const name = labels[region];
  const code = region.toUpperCase();
  return name ? `${name} ${code}` : code;
}

function ShopDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm leading-snug">
      <span className="shrink-0 text-slate-400">{label}</span>
      <span className="min-w-0 break-all text-right font-medium text-slate-700">{value}</span>
    </div>
  );
}

function AuthorizedShopCard({
  shop,
  onUnbind,
}: {
  shop: AuthorizedShop;
  onUnbind: (shop: AuthorizedShop) => void;
}) {
  const { t } = useTranslation();
  const theme = regionThemes[shop.region] ?? defaultRegionTheme;
  const typeTag =
    shop.shopType === 'local'
      ? { text: t('store.shop_local'), className: 'bg-emerald-50 text-emerald-600' }
      : shop.shopType === 'cross'
        ? { text: t('store.shop_cross'), className: 'bg-orange-50 text-orange-600' }
        : null;

  return (
    <article className="relative flex h-full flex-col overflow-hidden rounded-xl border border-[#eef0f4] bg-white shadow-[0_1px_4px_rgba(15,23,42,0.05)]">
      <div className={`h-0.5 w-full shrink-0 `} aria-hidden />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3">
          <span
            className={`inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white ${theme.iconBg}`}
          >
            {shop.regionCode}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="m-0 truncate text-base font-semibold text-slate-800">{shop.name}</h3>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-[8px] bg-emerald-50 px-2 py-1 text-xs font-medium text-[#10b981]">
                <CheckOutlined className="text-[11px]" />
                {t('store.authorized')}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${theme.regionTag}`}>
                {shop.regionLabel}
              </span>
              {typeTag ? (
                <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${typeTag.className}`}>
                  {typeTag.text}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2.5">
          {shop.storeCode ? <ShopDetailRow label={t('store.store_code')} value={shop.storeCode} /> : null}
          {shop.sellerEmail ? <ShopDetailRow label={t('store.seller_email')} value={shop.sellerEmail} /> : null}
          {shop.authorizedAt ? <ShopDetailRow label={t('store.authorized_at')} value={shop.authorizedAt} /> : null}
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#596ae8]/[0.06] px-3 py-2.5 text-xs leading-snug text-[#7a83ad]">
          <MessageOutlined className="shrink-0 text-sm" aria-hidden />
          <span>
            {t('store.can_send')}<span className="text-[#596ae8] font-bold">{t('store.channel_dm')}</span>{t('store.can_send_mid')}<span className="text-[#596ae8] font-bold">{t('store.channel_email')}</span>{t('store.can_send_end')}<span className="text-[#596ae8] font-bold">{t('store.targeted_invite')}</span>
          </span>
        </div>

        <div className="mt-auto flex pt-4">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
            onClick={() => onUnbind(shop)}
          >
            <CloseOutlined className="text-xs" />
            {t('store.unbind')}
          </button>
        </div>
      </div>
    </article>
  );
}

function UnbindShopModal({
  shop,
  confirming,
  onCancel,
  onConfirm,
}: {
  shop: AuthorizedShop | null;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('store.unbind_title')}
      open={shop !== null}
      onCancel={onCancel}
      destroyOnClose
      width={480}
      centered
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="primary" danger loading={confirming} onClick={onConfirm}>
            {t('store.unbind_confirm')}
          </Button>
        </div>
      }
    >
      <div className="mb-5 flex gap-2.5  text-sm leading-relaxed text-slate-600">
        <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-[#596ae8]/40 text-[#596ae8]">
          <ExclamationOutlined className="text-[10px]" />
        </span>
        <p className="m-0">
          {t('store.unbind_desc')}
        </p>
      </div>

    </Modal>
  );
}

function EmptyShopSlotCard({
  onAuthorize,
  onPurchaseSlot,
}: {
  onAuthorize: () => void;
  onPurchaseSlot: () => void;
}) {
  const { t } = useTranslation();

  return (
    <article className="flex h-full flex-col rounded-xl border-2 border-dashed border-[#e8edf3] bg-[repeating-linear-gradient(135deg,#ffffff_0,#ffffff_10px,#fafbfc_10px,#fafbfc_20px)] p-5">
      <div className="flex flex-1 flex-col items-center justify-center px-2 text-center">
        <span className="inline-flex size-12 items-center justify-center rounded-xl bg-[#596ae8]/10 text-2xl font-light text-[#596ae8]">
          <PlusOutlined />
        </span>
        <p className="mb-1 mt-4 text-base font-semibold text-slate-700">{t('store.empty_slot')}</p>
        <p className="m-0 text-xs text-slate-400">{t('store.sea_only')}</p>
      </div>
      <div className="mt-auto flex gap-2 pt-4">
        <Button type="primary" className="wb-btn-primary flex-1" onClick={onAuthorize}>
          {t('store.authorize_new')}
        </Button>
        <button
          type="button"
          className="flex-1 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
          onClick={onPurchaseSlot}
        >
          {t('store.purchase_slot')}
        </button>
      </div>
    </article>
  );
}

function AuthorizeShopModal({
  open,
  market,
  marketOptions,
  onMarketChange,
  onCancel,
  onConfirm,
  confirming,
}: {
  open: boolean;
  market: string;
  marketOptions: { value: string; label: string }[];
  onMarketChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('store.authorize_title')}
      open={open}
      onCancel={onCancel}
      destroyOnClose
      width={480}
      centered
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="primary" className="wb-btn-primary" loading={confirming} onClick={onConfirm}>
            {t('store.go_authorize')}
          </Button>
        </div>
      }
    >
      <div className="mb-5 flex gap-2.5 rounded-lg border border-[#596ae8]/15 bg-[#596ae8]/[0.06] px-3.5 py-3 text-sm leading-relaxed text-slate-600">
        <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-[#596ae8]/40 text-[#596ae8]">
          <ExclamationOutlined className="text-[10px]" />
        </span>
        <p className="m-0">
          {t('store.authorize_desc')}
        </p>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-800">
          {t('store.auth_market')} <span className="text-red-500">*</span>
        </p>
        <Select
          value={market}
          options={marketOptions}
          onChange={onMarketChange}
          className="w-full"
          placeholder={t('store.auth_market_placeholder')}
        />
        <p className="mb-0 mt-2 text-xs text-slate-400">{t('store.auth_market_hint')}</p>
      </div>
    </Modal>
  );
}

export const StoreAuthorization = observer(() => {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { reloadShops } = useSelectedShop();
  const [authorizeOpen, setAuthorizeOpen] = useState(false);
  const [authorizeMarket, setAuthorizeMarket] = useState('vn');
  const [marketFilter, setMarketFilter] = useState('all');
  const [storeTypeFilter, setStoreTypeFilter] = useState('all');
  const [unbindShop, setUnbindShop] = useState<AuthorizedShop | null>(null);
  const [unbinding, setUnbinding] = useState(false);
  const [sellerShops, setSellerShops] = useState<SellerAuthShop[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopReload, setShopReload] = useState(0);
  const [authorizing, setAuthorizing] = useState(false);

  useEffect(() => {
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setSellerShops([]);
      setShopsLoading(account == null);
      return;
    }
    let cancelled = false;
    setShopsLoading(true);
    getSellerShops({ user_code: userCode })
      .then((res) => {
        if (cancelled) return;
        if (res.code === 200 && Array.isArray(res.content)) {
          setSellerShops(res.content);
        } else {
          if (res.code !== -1) message.error(res.message || t('store.load_failed'));
          setSellerShops([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('store.load_failed'));
          setSellerShops([]);
        }
      })
      .finally(() => {
        if (!cancelled) setShopsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [account, shopReload, t]);

  const slots = useMemo((): ShopSlot[] => {
    const labels: Record<string, string> = {
      vn: t('store.market_vn'),
      th: t('store.market_th'),
      id: t('store.market_id'),
      my: t('store.market_my'),
      ph: t('store.market_ph'),
      sg: t('store.market_sg')
    };
    const shops = sellerShops.flatMap((shop, index): AuthorizedShop[] => {
      if (shop.authCancelled === 1) return [];
      const regionCode = (shop.shopRegion ?? '').trim().toUpperCase();
      const region = regionCode.toLowerCase();
      return [
        {
          kind: 'shop',
          id: shop.shopId?.trim() || shop.shopCode?.trim() || `shop-${index}`,
          shopId: shop.shopId?.trim() || '',
          region,
          regionCode: regionCode || '—',
          regionLabel: marketLabel(region, labels),
          name: shop.shopName?.trim() || '—',
          storeCode: shop.shopCode?.trim() || ''
        }
      ];
    });
    const capacity = account?.quota?.shop?.total;
    const emptyCount = capacity == null ? 0 : Math.max(0, capacity - shops.length);
    const empties: EmptySlot[] = Array.from({ length: emptyCount }, (_, index) => ({
      kind: 'empty',
      id: `empty-${index + 1}`
    }));
    return [...shops, ...empties];
  }, [account?.quota?.shop?.total, sellerShops, t]);

  const marketFilterOptions = useMemo(
    () => [
      { value: 'all', label: t('store.all') },
      { value: 'vn', label: t('store.market_vn') },
    ],
    [t]
  );

  const storeTypeFilterOptions = useMemo(
    () => [
      { value: 'all', label: t('store.all') },
      { value: 'cross', label: t('store.type_cross') },
      { value: 'local', label: t('store.type_local') },
    ],
    [t]
  );

  const authorizeMarketOptions = useMemo(
    () => marketFilterOptions.filter((item) => item.value !== 'all'),
    [marketFilterOptions]
  );

  const openAuthorizeModal = () => {
    setAuthorizeMarket('vn');
    setAuthorizeOpen(true);
  };

  const closeAuthorizeModal = () => {
    setAuthorizeOpen(false);
  };

  const submitAuthorize = async () => {
    const userCode = account?.userCode?.trim() ?? '';
    const region = authorizeMarket.trim().toUpperCase();
    if (!userCode || !region) {
      message.error(t('store.authorize_failed'));
      return;
    }
    setAuthorizing(true);
    try {
      const res = await createSellerAuthorizationUrl({ region, userCode });
      const authorizationUrl = res.code === 200 ? res.content?.authorizationUrl?.trim() : '';
      if (!authorizationUrl) {
        message.error(res.message || t('store.authorize_failed'));
        return;
      }
      window.open(authorizationUrl, '_blank', 'noopener,noreferrer');
      setAuthorizeOpen(false);
    } catch {
      message.error(t('store.authorize_failed'));
    } finally {
      setAuthorizing(false);
    }
  };
  const filteredSlots = useMemo(() => {
    return slots.filter((slot) => {
      if (slot.kind === 'empty') {
        return true;
      }
      if (marketFilter !== 'all' && slot.region !== marketFilter) {
        return false;
      }
      if (storeTypeFilter !== 'all' && slot.shopType && slot.shopType !== storeTypeFilter) {
        return false;
      }
      return true;
    });
  }, [marketFilter, slots, storeTypeFilter]);

  const handleUnbind = (shop: AuthorizedShop) => {
    setUnbindShop(shop);
  };

  const closeUnbindModal = () => {
    if (unbinding) return;
    setUnbindShop(null);
  };

  const submitUnbind = async () => {
    const shopId = unbindShop?.shopId.trim() ?? '';
    if (!unbindShop || unbinding) return;
    if (!shopId) {
      message.error(t('store.unbind_failed'));
      return;
    }
    setUnbinding(true);
    try {
      const res = await unbindSellerShop({ shopId });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('store.unbind_failed'));
        return;
      }
      message.success(res.message || t('store.unbind_success', { name: unbindShop.name }));
      setUnbindShop(null);
      setShopReload((value) => value + 1);
      reloadShops();
    } catch {
      message.error(t('store.unbind_failed'));
    } finally {
      setUnbinding(false);
    }
  };

  return (
    <Layout className="container ">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h1 className="mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
          {t('store.title')}
        </h1>

      </header>

      <TagFilterPanel
        className="mb-4"
        rows={[
          {
            key: 'market',
            label: t('store.market'),
            chipPrefix: t('store.market'),
            value: marketFilter,
            options: marketFilterOptions,
            onChange: setMarketFilter,
          },
          // {
          //   key: 'storeType',
          //   label: t('store.store_type'),
          //   chipPrefix: t('store.store_type'),
          //   value: storeTypeFilter,
          //   options: storeTypeFilterOptions,
          //   onChange: setStoreTypeFilter,
          // },
        ]}
      />

      <section className="wb-section">
        <UnbindShopModal
          shop={unbindShop}
          confirming={unbinding}
          onCancel={closeUnbindModal}
          onConfirm={submitUnbind}
        />
        <AuthorizeShopModal
          open={authorizeOpen}
          market={authorizeMarket}
          marketOptions={authorizeMarketOptions}
          onMarketChange={setAuthorizeMarket}
          onCancel={closeAuthorizeModal}
          onConfirm={submitAuthorize}
          confirming={authorizing}
        />
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="wb-btn-primary"
            onClick={openAuthorizeModal}
          >
            {t('store.add_auth')}
          </Button>
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <SafetyOutlined />
            {t('store.safety_hint')}
          </span>
        </div>

        {shopsLoading ? (
          <div className="flex min-h-[220px] items-center justify-center py-10">
            <Spin />
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-4 min-[900px]:grid-cols-2 min-[1280px]:grid-cols-3">
            {filteredSlots.map((slot) =>
              slot.kind === 'shop' ? (
                <div key={slot.id} className="h-full min-h-0">
                  <AuthorizedShopCard shop={slot} onUnbind={handleUnbind} />
                </div>
              ) : (
                <div key={slot.id} className="h-full min-h-0">
                  <EmptyShopSlotCard
                    onAuthorize={() => {
                      setAuthorizeMarket('vn');
                      setAuthorizeOpen(true);
                    }}
                    onPurchaseSlot={() => message.info(t('store.purchase_slot'))}
                  />
                </div>
              )
            )}
          </div>
        )}
      </section>
    </Layout>
  );
});
